// Claude: Department stats page — open RFXs, buyers, sellers responded per department; sellers targeting + unassigned row 04-05-2026
import React, { useEffect, useState } from "react";
import { Table } from "antd";
import { Button, Flex, Text } from "@chakra-ui/react";
import { MdChevronLeft } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

const pinUnassigned = (a: any, b: any) => {
  if (a._id === "unassigned") return -1;
  if (b._id === "unassigned") return 1;
  return 0;
};

const columns = [
  {
    title: "Department",
    dataIndex: "name",
    key: "name",
    sorter: (a: any, b: any) => pinUnassigned(a, b) || a.name.localeCompare(b.name),
  },
  {
    title: "Sellers Targeting",
    dataIndex: "sellerTargetingCount",
    key: "sellerTargetingCount",
    sorter: (a: any, b: any) => pinUnassigned(a, b) || a.sellerTargetingCount - b.sellerTargetingCount,
    align: "center" as const,
  },
  {
    title: "Open RFPs",
    dataIndex: "openRfxCount",
    key: "openRfxCount",
    sorter: (a: any, b: any) => pinUnassigned(a, b) || a.openRfxCount - b.openRfxCount,
    defaultSortOrder: "descend" as const,
    align: "center" as const,
    render: (val: any) => val === null ? "—" : val,
  },
  {
    title: "Buyers",
    dataIndex: "buyerCount",
    key: "buyerCount",
    sorter: (a: any, b: any) => pinUnassigned(a, b) || a.buyerCount - b.buyerCount,
    align: "center" as const,
    render: (val: any) => val === null ? "—" : val,
  },
  {
    title: "Sellers Responded",
    dataIndex: "sellerRespondedCount",
    key: "sellerRespondedCount",
    sorter: (a: any, b: any) => pinUnassigned(a, b) || a.sellerRespondedCount - b.sellerRespondedCount,
    align: "center" as const,
    render: (val: any) => val === null ? "—" : val,
  },
];

const DepartmentStats = () => {
  const navigate = useNavigate();
  const { error } = useToastWrapper();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await AdminAPI.getDepartmentStats();
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        setData(rows.map((r: any) => ({ ...r, key: r._id })));
      } catch (err: any) {
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalSellerTargeting = data.reduce((sum, r) => sum + (r.sellerTargetingCount || 0), 0);
  const totalOpenRfx = data.reduce((sum, r) => sum + (r.openRfxCount || 0), 0);
  const totalBuyers = data.reduce((sum, r) => sum + (r.buyerCount || 0), 0);
  const totalSellers = data.reduce((sum, r) => sum + (r.sellerRespondedCount || 0), 0);

  return (
    <div className="p-4">
      <Flex align="center" mb={4}>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<MdChevronLeft size={18} />}
          onClick={() => navigate("/admin/departments")}
          _hover={{ bg: "gray.100" }}
        >
          Back
        </Button>
        <Text fontSize="2xl" fontWeight={600} ml={2}>Department Stats</Text>
      </Flex>

      <Text fontSize="11px" color="gray.500" mb={2} fontStyle="italic">
        Counts are based on open RFPs only. Buyers and Sellers Responded show distinct counts.
      </Text>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        size="middle"
        bordered
        summary={() => (
          <Table.Summary.Row style={{ fontWeight: 600, background: "#F9FAFB" }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="center">{totalSellerTargeting}</Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="center">{totalOpenRfx}</Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="center">{totalBuyers}</Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="center">{totalSellers}</Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default DepartmentStats;
